import axios from "axios";
import {
  addDays,
  getDaysInMonth,
  isAfter,
  isBefore,
  set,
  subDays,
} from "date-fns";
import type { MenuItemProps } from "../components/CollapsableMenu/types";

// Função para calcular o MDC (Máximo Divisor Comum) usando o Algoritmo de Euclides
export const mdc = (a: number, b: number) => {
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  return a;
};

// Função para calcular o MMC (Mínimo Múltiplo Comum) de dois números
export const mmc = (a: number, b: number) => {
  return Math.abs(a * b) / mdc(a, b);
};

// Função para calcular o MMC de um array de números
export const mmcArray = (numbers: number[]) => {
  return numbers.reduce((acc, num) => mmc(acc, num), 1);
};

interface PriceOptions {
  hasPrefix?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}
// Função para formatar preço
export const formatPrice = (price: number | string, options?: PriceOptions) => {
  if (price === undefined || price === null) return "";

  const hasDecimal =
    price.toString().includes(".") || price.toString().includes(",");

  if (typeof price === "string") {
    price = price.replace(/\./g, "").replace(",", "");
  }

  const parsedPrice =
    typeof price === "string"
      ? parseFloat(price) / (hasDecimal ? 100 : 1)
      : price;

  const amount = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    ...options,
  }).format(parsedPrice);

  return options?.hasPrefix === false ? `${amount}`.substring(3) : `${amount}`;
};

// Função para mascarar uma string
export const maskString = (string: string, percent = 50, char = "*") => {
  const length = string.length;
  const charAmount = length * (percent / 100);
  const firstAmount = Math.floor((length - charAmount) / 2);

  return (
    string.substring(0, firstAmount) +
    char.repeat(charAmount) +
    string.substring(firstAmount + charAmount)
  );
};

// Função para limpar telefone
export const clearPhone = (phone: string) => {
  return phone.replace(/\D/g, "");
};

// Função para validar um CPF
export const validateCPF = (cpf: string) => {
  if (cpf.length !== 11) {
    return false;
  }

  if (cpf.match(/(\d)\1{10}/)) {
    return false;
  }

  const numbers = cpf.substring(0, 9);
  const digits = cpf.substring(9);

  const sum = numbers
    .split("")
    .map((number, index) => parseInt(number, 10) * (10 - index))
    .reduce((acc, curr) => acc + curr);

  const firstDigit = (sum * 10) % 11;

  if (firstDigit.toString() !== digits.charAt(0)) {
    return false;
  }

  const sum2 = numbers
    .split("")
    .map((number, index) => parseInt(number, 10) * (11 - index))
    .reduce((acc, curr) => acc + curr);

  const secondDigit = (sum2 * 10) % 11;

  return secondDigit.toString() === digits.charAt(1);
};

// Função para buscar endereço pelo cep
export const getAddressByCep = async (cep: string) => {
  const formattedCep = cep.replace(/(\.|-)/gi, "");
  try {
    const { data } = await axios.get(
      `https://viacep.com.br/ws/${formattedCep}/json/`,
    );

    return { ...data, provider: "viacep" };
  } catch (_err) {
    const { data } = await axios.get(
      `https://brasilapi.com.br/api/cep/v2/${cep}`,
    );

    return {
      cep: data.cep,
      logradouro: data.street,
      bairro: data.neighborhood,
      localidade: data.city,
      uf: data.state,
      provider: "brasilapi",
      brasilapi_service: data.service,
    };
  }
};

// Função para converter HEX para RGB
export const hexToRgb = (hex: string): [number, number, number] => {
  // Remove o '#' se presente
  hex = hex.replace(/^#/, "");

  // Converter HEX para os componentes RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return [r, g, b];
};

// Função para converter RGB para HSB
export const rgbToHsb = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const v = Math.max(r, g, b),
    n = v - Math.min(r, g, b);
  const h =
    n === 0
      ? 0
      : n && v === r
        ? (g - b) / n
        : v === g
          ? 2 + (b - r) / n
          : 4 + (r - g) / n;
  return [60 * (h < 0 ? h + 6 : h), v && (n / v) * 100, v * 100];
};

// Função para converter HEX para HSB
export const hexToHsb = (hex: string): [number, number, number] => {
  // Remove o '#' se presente
  hex = hex.replace(/^#/, "");

  // Converter HEX para RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Calcular o máximo e mínimo entre R, G, B
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const delta = max - min;

  // Calcular brilho (B)
  const bValue = max * 100;

  // Calcular saturação (S)
  const sValue = max === 0 ? 0 : (delta / max) * 100;

  // Calcular matiz (H)
  let hValue = 0;
  if (delta !== 0) {
    if (max === r) hValue = (g - b) / delta + (g < b ? 6 : 0);
    else if (max === g) hValue = (b - r) / delta + 2;
    else hValue = (r - g) / delta + 4;

    hValue = Math.round(hValue * 60);
  }

  return [hValue, Math.round(sValue), Math.round(bValue)];
};

export const rgbToHex = (number: number) =>
  number.toString(16).padStart(2, "0");

// Função para converter HSB para HEX
const hsbToHex = (h: number, s: number, b: number): string => {
  s /= 100;
  b /= 100;

  const c = b * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = b - c;
  let r = 0,
    g = 0,
    bl = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    bl = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    bl = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    bl = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    bl = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    bl = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    bl = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  bl = Math.round((bl + m) * 255);

  return `#${rgbToHex(r)}${rgbToHex(g)}${rgbToHex(bl)}`;
};

interface ColorVariants {
  lightest: string;
  lighter: string;
  light: string;
  default: string;
  dark: string;
  darker: string;
}

// Cache for color variants with LRU eviction (max 50 entries)
const colorVariantsCache = new Map<string, ColorVariants>();
const MAX_CACHE_SIZE = 50;

const cacheColorVariants = (
  hex: string,
  variants: ColorVariants,
): ColorVariants => {
  if (colorVariantsCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry (first inserted)
    const firstKey = colorVariantsCache.keys().next().value;
    colorVariantsCache.delete(firstKey);
  }
  colorVariantsCache.set(hex, variants);
  return variants;
};

// Função para gerar variações de cor
export const generateColorVariants = (hex: string): ColorVariants => {
  // Check cache first
  const cached = colorVariantsCache.get(hex);
  if (cached) {
    return cached;
  }

  // Converter de HEX para HSB
  const [h, s, b] = hexToHsb(hex);

  // Gerar cor mais clara: -20 de S, +4 de L
  const lightS = Math.max(s - 20, 0); // Garantir que S não seja negativo
  const lightB = Math.min(b + 4, 100); // Garantir que L não passe de 100
  const lightColor = hsbToHex(h, lightS, lightB);

  // Gerar cor ainda mais clara: -40 de S, +8 de L
  const lighterS = Math.max(s - 40, 0); // Garantir que S não seja negativo
  const lighterB = Math.min(b + 8, 100); // Garantir que L não passe de 100
  const lighterColor = hsbToHex(h, lighterS, lighterB);

  // Gerar cor ainda mais clara: -60 de S, +12 de L
  const lightestS = Math.max(s - 60, 0); // Garantir que S não seja negativo
  const lightestB = Math.min(b + 12, 100); // Garantir que L não passe de 100
  const lightestColor = hsbToHex(h, lightestS, lightestB);

  // Gerar cor mais escura: +4 de S, -20 de L
  const darkS = Math.min(s + 4, 100); // Garantir que S não passe de 100
  const darkB = Math.max(b - 20, 0); // Garantir que L não seja negativo
  const darkColor = hsbToHex(h, darkS, darkB);

  // Gerar cor ainda mais escura: +8 de S, -40 de L
  const darkerS = Math.min(s + 8, 100); // Garantir que S não passe de 100
  const darkerB = Math.max(b - 40, 0); // Garantir que L não seja negativo
  const darkerColor = hsbToHex(h, darkerS, darkerB);

  const variants: ColorVariants = {
    lightest: lightestColor,
    lighter: lighterColor,
    light: lightColor,
    default: hex,
    dark: darkColor,
    darker: darkerColor,
  };

  return cacheColorVariants(hex, variants);
};

export const getBrighterColor = (hex: string, percentage: number) => {
  let [r, g, b] = hexToRgb(hex);

  const lightenChannel = (channel: number) =>
    Math.round(channel + (255 - channel) * (percentage / 100));

  r = lightenChannel(r);
  g = lightenChannel(g);
  b = lightenChannel(b);

  return `#${rgbToHex(r)}${rgbToHex(g)}${rgbToHex(b)}`;
};

// Função para preencher um array de datas
export const fillDaysArray = (arr: Array<Date>) => {
  const firstDay = arr[0];
  for (let i = 1; i <= firstDay.getDay(); i++) {
    const date = subDays(firstDay, i);
    arr.unshift(date);
  }

  const multiplier = Math.ceil(arr.length / 7);
  const left = 7 * multiplier - arr.length;
  const lastDay = arr[arr.length - 1];

  for (let i = 1; i <= left; i++) {
    const date = addDays(lastDay, i);
    arr.push(date);
  }
};

// Função para aceitar apenas horas validas numa string e ajustar conforme necessário
export const clampTimeString = (
  timeStr: string,
  currentDate: Date,
  type: "start" | "end",
  compareDate?: Date,
) => {
  const [hourStr, minuteStr] = timeStr.split(":");

  let hours = parseInt(hourStr, 10);
  let minutes = parseInt(minuteStr, 10);

  // Ajuste para valores válidos de horas e minutos
  hours = Math.max(0, Math.min(23, hours));
  minutes = Math.max(0, Math.min(59, minutes));

  // Define a data ajustada com partes preservadas de currentDate
  const adjustedDate = set(currentDate, { hours, minutes });

  // Verifica e aplica restrições
  if (compareDate && type === "start" && isAfter(adjustedDate, compareDate)) {
    return compareDate;
  } else if (
    compareDate &&
    type === "end" &&
    isBefore(adjustedDate, compareDate)
  ) {
    return compareDate;
  }

  return adjustedDate;
};

// Função para aceitar apenas datas validas numa string e ajustar conforme necessário
export const clampDateString = (
  dateStr: string,
  currentDate: Date,
  type: "start" | "end",
  compareDate?: Date,
) => {
  const [dayStr, monthStr, yearStr] = dateStr.split("/");

  let day = parseInt(dayStr, 10);
  let month = parseInt(monthStr, 10) - 1;
  const year = parseInt(yearStr, 10);

  // Ajuste do mês e do dia
  month = Math.max(0, Math.min(11, month));
  const maxDay = getDaysInMonth(new Date(year, month));
  day = Math.max(1, Math.min(maxDay, day));

  // Define a data ajustada com partes preservadas de currentDate
  const adjustedDate = set(currentDate, { year, month, date: day });

  if (compareDate && type === "start" && isAfter(adjustedDate, compareDate)) {
    return compareDate;
  } else if (
    compareDate &&
    type === "end" &&
    isBefore(adjustedDate, compareDate)
  ) {
    return compareDate;
  }

  return adjustedDate;
};

export function currencyFormatter(
  value: number,
  hasPrefix?: boolean,

  minimumFractionDigits = 2,
  max?: number,
  min?: number,
) {
  if (value === null || value === undefined || Number.isNaN(value)) return "";

  if (max && value > max * 100) {
    value = max * 100;
  }

  if (min && value < min * 100) {
    value = min * 100;
  }

  const amount = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits,
  }).format(value / 10 ** minimumFractionDigits);

  return hasPrefix ? `${amount}` : `${amount}`.substring(3);
}

type Complement = {
  price: string;
  amount: number;
};

type ComplementCategory = {
  additional: boolean;
  more_expensive_only: boolean;
  use_average: boolean;
  complements: Complement[];
};

export type Product = {
  price: string;
  amount: number;
  weight?: number;
  complement_categories?: ComplementCategory[];
};

export const getComplementsPrice = (product: Product) => {
  const basePrice =
    parseFloat(product.price) * product.amount * (product.weight || 1);

  const complementsPrice =
    product.complement_categories?.reduce((catTotal, cat) => {
      if (!cat.additional) return catTotal;

      if (cat.more_expensive_only) {
        const highestComplement = cat.complements.reduce(
          (highest, c) => {
            return parseFloat(c.price) > parseFloat(highest.price)
              ? c
              : highest;
          },
          { price: "0", amount: 0 },
        );

        return (
          catTotal +
          parseFloat(highestComplement.price) *
            (highestComplement.amount >= 1 ? 1 : 0) *
            product.amount
        );
      }

      if (cat.use_average) {
        const totalValue = cat.complements.reduce(
          (sum, c) => sum + parseFloat(c.price) * c.amount * product.amount,
          0,
        );
        const totalAmount = cat.complements.reduce(
          (sum, c) => sum + c.amount * product.amount,
          0,
        );

        return totalAmount > 0 ? catTotal + totalValue / totalAmount : catTotal;
      }

      const complementsValue = cat.complements.reduce(
        (sum, c) => sum + parseFloat(c.price) * c.amount * product.amount,
        0,
      );

      return catTotal + complementsValue;
    }, 0) ?? 0;

  return basePrice + complementsPrice;
};

export const formatSearchItems = (
  menuItems: MenuItemProps[],
  parentPath = "",
  parentTexts: string[] = [],
): MenuItemProps[] => {
  return menuItems
    .filter((item) => item.condition !== false)
    .flatMap((item) => {
      const currentPath = parentPath + (item.path || "");
      const currentTexts = [...parentTexts, item.text];

      if (item.subitems && item.subitems.length > 0) {
        return formatSearchItems(item.subitems, currentPath, currentTexts);
      }

      return [
        {
          ...item,
          path: currentPath,
          searchText: currentTexts.join("/"),
          key: `${currentPath}|${currentTexts.join("/")}`,
        },
      ];
    });
};

export const filterItems = (item: MenuItemProps, search: string) => {
  return item.searchText?.toLowerCase().includes(search.toLowerCase());
};

export const filterSidebarItems = (items: MenuItemProps[]) => {
  return items
    .map((item) => {
      if (item.subitems) {
        return {
          ...item,
          subitems: item.subitems.filter(
            (subitem) =>
              (subitem.displayType === "sidebar" || !subitem.displayType) &&
              subitem.condition !== false,
          ),
        };
      }
      return item;
    })
    .filter(
      (item) =>
        (item.displayType === "sidebar" || !item.displayType) &&
        item.condition !== false,
    );
};
